/**
 * media-gallery.js — Ella 7.0.0 (Halothemes)
 * ---------------------------------------------------------------------------
 * COLOUR-FILTERED PRODUCT GALLERY
 *
 * Shows only the media belonging to the currently selected colour, the way
 * ssactivewear.com does it. Grouping is driven by the IMAGE ALT TEXT, so no
 * Liquid changes and no metafields are required.
 *
 * ALT TEXT — just put the colour name in it. All of these work:
 *
 *     White                     Gold - front
 *     White - front             " Gold - front"   (stray spaces are fine)
 *     white front               Gold_front
 *     Front view of White tee   GOLD — FRONT      (any dash, any case)
 *
 * The alt text is compared against the colour values the product actually
 * offers and the LONGEST colour name found in it wins, so
 * "Antique Cherry Red - front" matches Antique Cherry Red, never plain Red,
 * and "Turf Green" never collides with "Green". Images naming no colour are
 * SHARED and stay visible for every colour. A colour with no images of its own
 * falls back to showing everything rather than going blank.
 *
 * COLOUR SELECTORS SUPPORTED (whichever the product uses):
 *   1. .ri-item[data-color]        — the Roman Imprint "Product Color" carousel
 *   2. <variant-selects>           — Ella's own swatch / pill / dropdown picker
 *   3. .ai-color-carousel__item    — the AI-generated swatch carousel block
 *
 * PERFORMANCE NOTES
 *   • A swatch click filters synchronously, in the same tick as the click, so
 *     the thumbnails change at the same moment as the main image.
 *   • Slide and thumbnail elements are MOVED, never cloned, so the browser
 *     never re-decodes an image that is already on screen.
 *   • Any image that genuinely still has to load shows a shimmer placeholder
 *     and only fades in once it has decoded.
 *
 * This file replaces the empty <media-gallery> stub that shipped with the
 * theme. The custom element is still defined so the theme's markup and any
 * other script referencing it keep working.
 * ---------------------------------------------------------------------------
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Config                                                              */
  /* ------------------------------------------------------------------ */

  var COLOR_OPTION_NAMES = [
    'color',
    'colour',
    'shirt color',
    'shirt colour',
    'product color',
    'product colour',
    'garment color',
    'garment colour'
  ];

  var RI_ITEM = '.ri-item[data-color]';
  var AI_ITEM = '[data-color][data-option-index], [class*="ai-color-carousel__item"][data-color]';
  var ANY_SWATCH = RI_ITEM + ', ' + AI_ITEM;

  // Only used for background re-checks; a click is applied immediately.
  var DEBOUNCE_MS = 120;

  // Set to false once you are happy it works.
  var DEBUG = true;

  /* ------------------------------------------------------------------ */
  /* Loading placeholder styles                                          */
  /* ------------------------------------------------------------------ */

  var STYLE_ID = 'media-gallery-filter-styles';

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      'media-gallery .swiper-controls__thumbnail,',
      'media-gallery .product-media { position: relative; }',

      'media-gallery .mg-loading::after {',
      '  content: ""; position: absolute; inset: 0; border-radius: inherit;',
      '  background: linear-gradient(90deg,',
      '    rgba(0,0,0,0.045) 25%, rgba(0,0,0,0.09) 37%, rgba(0,0,0,0.045) 63%);',
      '  background-size: 400% 100%;',
      '  animation: mg-shimmer 1.1s ease-in-out infinite;',
      '  pointer-events: none; z-index: 1;',
      '}',

      'media-gallery .mg-loading > img,',
      'media-gallery .mg-loading picture img { opacity: 0; }',

      'media-gallery .swiper-controls__thumbnail img,',
      'media-gallery .product-media img { transition: opacity 0.18s ease; }',

      '@keyframes mg-shimmer {',
      '  0%   { background-position: 100% 50%; }',
      '  100% { background-position: 0 50%; }',
      '}',

      '@media (prefers-reduced-motion: reduce) {',
      '  media-gallery .mg-loading::after { animation: none; }',
      '}'
    ].join('\n');
    document.head.appendChild(style);
  }

  /**
   * Show a shimmer on any image that has not decoded yet, and clear it the
   * moment it has. Images already in memory never flash a placeholder.
   */
  function trackLoading(elements) {
    elements.forEach(function (el) {
      var img = el.querySelector('img');
      if (!img) return;

      if (img.complete && img.naturalWidth > 0) {
        el.classList.remove('mg-loading');
        return;
      }

      el.classList.add('mg-loading');
      var done = function () {
        el.classList.remove('mg-loading');
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
      };
      img.addEventListener('load', done);
      img.addEventListener('error', done);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Text helpers                                                        */
  /* ------------------------------------------------------------------ */

  function norm(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ['[gallery]'].concat([].slice.call(arguments)));
    }
  }

  /**
   * Which colour does this alt text name?
   * `keys` must be normalised and sorted longest-first.
   * Returns '' when it names none (a shared image).
   */
  function matchColor(alt, keys) {
    var text = norm(alt);
    if (!text) return '';

    // Padding both ends turns "contains this whole word sequence" into one
    // substring test that covers start, middle, end and exact matches.
    var padded = ' ' + text + ' ';

    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (key && padded.indexOf(' ' + key + ' ') !== -1) return key;
    }
    return '';
  }

  function altOf(el) {
    var img = el.querySelector('img[alt]');
    return img ? img.getAttribute('alt') || '' : '';
  }

  function uniq(list) {
    var seen = {};
    var out = [];
    list.forEach(function (value) {
      if (value && !seen[value]) {
        seen[value] = true;
        out.push(value);
      }
    });
    return out;
  }

  function sortedColorKeys(values) {
    return uniq(values.map(norm)).sort(function (a, b) {
      return b.length - a.length; // longest first: "turf green" beats "green"
    });
  }

  /* ------------------------------------------------------------------ */
  /* Reading Ella's own variant picker                                   */
  /* ------------------------------------------------------------------ */

  function optionLabelName(optionEl) {
    var label = optionEl.querySelector('.form__label');
    if (!label) return '';
    var text = '';
    for (var i = 0; i < label.childNodes.length; i++) {
      if (label.childNodes[i].nodeType === 3) text += label.childNodes[i].textContent;
    }
    return text.replace(/:/g, ' ').trim();
  }

  function optionSelectedValue(optionEl) {
    var radio = optionEl.querySelector('input[type="radio"]:checked');
    if (radio && radio.value) return radio.value.trim();

    var select = optionEl.querySelector('select');
    if (select && select.value) return select.value.trim();

    var span = optionEl.querySelector('[data-selected-value]');
    return span ? span.textContent.trim() : '';
  }

  function optionAllValues(optionEl) {
    var values = [];
    optionEl.querySelectorAll('input[type="radio"]').forEach(function (input) {
      if (input.value) values.push(input.value.trim());
    });
    if (!values.length) {
      optionEl.querySelectorAll('select option').forEach(function (option) {
        if (option.value) values.push(option.value.trim());
      });
    }
    return values;
  }

  function colorOptionGroup(scope) {
    var found = scope.querySelectorAll('.variant-option');
    if (!found.length) found = scope.querySelectorAll('.product-form__input');

    var groups = [];
    found.forEach(function (el) {
      if (el.querySelector('.variant-option')) return;
      groups.push(el);
    });

    var i;
    for (i = 0; i < groups.length; i++) {
      if (COLOR_OPTION_NAMES.indexOf(norm(optionLabelName(groups[i]))) !== -1) return groups[i];
    }
    for (i = 0; i < groups.length; i++) {
      if (/\bcolou?r\b/.test(norm(optionLabelName(groups[i])))) return groups[i];
    }
    return null;
  }

  /* ------------------------------------------------------------------ */
  /* Reading the swatch carousels                                        */
  /* ------------------------------------------------------------------ */

  function findAll(scope, selector) {
    var found = Array.prototype.slice.call(scope.querySelectorAll(selector));
    if (!found.length && scope !== document) {
      found = Array.prototype.slice.call(document.querySelectorAll(selector));
    }
    return found;
  }

  function activeSwatchValue(items, activeClass) {
    for (var i = 0; i < items.length; i++) {
      if (items[i].classList.contains(activeClass)) {
        return (items[i].getAttribute('data-color') || '').trim();
      }
    }
    return '';
  }

  /** Every colour control on the page, highest priority first. */
  function readControls(scope) {
    var controls = [];

    var riItems = findAll(scope, RI_ITEM);
    if (riItems.length) {
      controls.push({
        name: 'ri-carousel',
        selected: activeSwatchValue(riItems, 'is-active'),
        all: riItems.map(function (el) {
          return (el.getAttribute('data-color') || '').trim();
        })
      });
    }

    var group = colorOptionGroup(scope);
    if (group) {
      controls.push({
        name: 'variant-selects',
        selected: optionSelectedValue(group),
        all: optionAllValues(group)
      });
    }

    var aiItems = findAll(scope, AI_ITEM).filter(function (el) {
      return !el.matches(RI_ITEM);
    });
    if (aiItems.length) {
      controls.push({
        name: 'ai-carousel',
        selected: activeSwatchValue(aiItems, 'active'),
        all: aiItems.map(function (el) {
          return (el.getAttribute('data-color') || '').trim();
        })
      });
    }

    return controls;
  }

  /* ------------------------------------------------------------------ */
  /* Gallery controller                                                  */
  /* ------------------------------------------------------------------ */

  function isDuplicate(el) {
    return el.classList && el.classList.contains('swiper-slide-duplicate');
  }

  /**
   * Ask a live Swiper to re-measure after its slides changed underneath it.
   *
   * Deliberately NOT destroy + re-init. Swiper's own modules register their
   * cleanup through the same event bus the theme uses, so tearing an instance
   * down and rebuilding it left click handlers bound to a gutted object —
   * "Cannot read properties of undefined (reading 'clicked')" on the next tap.
   * update() re-reads the wrapper's children and fixes indexes, bullets and
   * arrows in place, which is both safer and faster.
   */
  function refreshSwiper(swiper) {
    if (!swiper || typeof swiper.update !== 'function') return;
    try {
      swiper.update();
    } catch (error) {
      log('swiper update failed', error);
    }
  }

  function ColorGallery(gallery) {
    this.gallery = gallery;
    this.scope = gallery.closest('product-info') || document;

    this.swiperComponent = gallery.querySelector('swiper-component');
    this.slidesWrapper =
      gallery.querySelector('.swiper-main > .swiper-wrapper') ||
      gallery.querySelector('.swiper__slides');
    this.thumbsWrapper = gallery.querySelector(
      '.swiper-controls__thumbnails-container .swiper-wrapper'
    );
    this.thumbsEl = gallery.querySelector(
      '.swiper-controls__thumbnails-container .swiper'
    );

    // Swiper's loop mode injects cloned slides. Never treat those as real
    // media, or a colour ends up with phantom thumbnails from its neighbours.
    this.allSlides = this.slidesWrapper
      ? Array.prototype.slice.call(this.slidesWrapper.children).filter(function (el) {
          return !isDuplicate(el);
        })
      : [];
    this.allThumbs = this.thumbsWrapper
      ? Array.prototype.slice.call(this.thumbsWrapper.children).filter(function (el) {
          return !isDuplicate(el);
        })
      : [];
    this.gridItems = Array.prototype.slice.call(
      gallery.querySelectorAll('ul.media-gallery__grid > li')
    );

    [this.allSlides, this.allThumbs, this.gridItems].forEach(function (list) {
      list.forEach(function (el) {
        el.__alt = altOf(el);
      });
    });

    this.appliedKey = null;
    this.lastSeen = {};
    this.manualKey = null; // a colour the shopper clicked, until one changes
    this.ownsThumbs = false;
    this.boundSlideChange = false;
  }

  /** Remember what every control currently reads, without acting on it. */
  ColorGallery.prototype.rememberControls = function (controls) {
    var self = this;
    (controls || readControls(this.scope)).forEach(function (control) {
      self.lastSeen[control.name] = norm(control.selected);
    });
  };

  /** Colour values this product offers, normalised and sorted longest-first. */
  ColorGallery.prototype.colorKeys = function (controls) {
    var all = [];
    (controls || readControls(this.scope)).forEach(function (control) {
      all = all.concat(control.all);
    });
    return sortedColorKeys(all);
  };

  /** Read whatever colour control exists and apply the matching filter. */
  ColorGallery.prototype.sync = function (force) {
    var controls = readControls(this.scope);
    if (!controls.length) return;

    var keys = this.colorKeys(controls);
    if (!keys.length) return;

    var self = this;
    var moved = null;
    var firstWithValue = null;

    controls.forEach(function (control) {
      var value = norm(control.selected);
      if (
        value &&
        self.lastSeen[control.name] !== undefined &&
        value !== self.lastSeen[control.name] &&
        !moved
      ) {
        moved = value;
      }
      if (value && !firstWithValue) firstWithValue = value;
      self.lastSeen[control.name] = value;
    });

    // A control the shopper just moved wins. Failing that, a colour they
    // clicked earlier stands — the Roman carousel updates the variant id but
    // not Ella's radios, so the untouched picker must not drag the gallery
    // back to the colour the page loaded on.
    if (moved) this.manualKey = null;
    var selected = moved || this.manualKey || firstWithValue;

    // Nothing chosen yet (the Roman carousel starts on "Select color") — use
    // the colour of the first image, which is what the page already shows.
    if (!selected && this.allSlides.length) {
      selected = matchColor(this.allSlides[0].__alt, keys);
    }
    if (!selected) return;

    this.apply(selected, keys, force);
  };

  ColorGallery.prototype.apply = function (selectedKey, colorKeys, force) {
    if (!force && selectedKey === this.appliedKey) return;

    var keyOf = function (el) {
      return matchColor(el.__alt, colorKeys);
    };

    var hasMatch = this.allSlides.some(function (slide) {
      return keyOf(slide) === selectedKey;
    });

    var keep = function (el) {
      if (!hasMatch) return true; // fail-safe: never show an empty gallery
      var key = keyOf(el);
      return key === selectedKey || key === '';
    };

    if (DEBUG) {
      log(
        'colour:', selectedKey,
        '| images for it:', this.allSlides.filter(function (s) { return keyOf(s) === selectedKey; }).length,
        '| shared:', this.allSlides.filter(function (s) { return keyOf(s) === ''; }).length,
        '| of', this.allSlides.length
      );
      if (!hasMatch) {
        log('no image matched "' + selectedKey + '" — showing all. Alt texts:',
          this.allSlides.map(function (s) { return s.__alt; }));
      }
    }

    // The colour's own images first, then the shared ones. A shared photo
    // placed before them in admin (a group shot of every colour) took the
    // first slide, so picking Black still opened on the group shot (Jason,
    // 30 Oct 2026, the Nolan bottle). Same order for slides and thumbnails,
    // so their indexes still line up.
    var ownFirst = function (list) {
      return list.filter(function (el) { return keyOf(el) === selectedKey; })
        .concat(list.filter(function (el) { return keyOf(el) !== selectedKey; }));
    };
    var slides = ownFirst(this.allSlides.filter(keep));
    var thumbs = ownFirst(this.allThumbs.filter(keep));
    var changed = false;

    // Elements are MOVED, not cloned, so nothing is re-downloaded or
    // re-decoded and there is no flash of empty thumbnails.
    if (this.slidesWrapper && this.childrenDiffer(this.slidesWrapper, slides)) {
      slides.forEach(function (slide) {
        var img = slide.querySelector('img');
        if (img && img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
      });
      this.slidesWrapper.replaceChildren.apply(this.slidesWrapper, slides);
      changed = true;
    }

    if (this.thumbsWrapper && this.childrenDiffer(this.thumbsWrapper, thumbs)) {
      thumbs.forEach(function (thumb, index) {
        thumb.setAttribute('data-index', String(index));
        thumb.classList.toggle('active', index === 0);
        if (index === 0) thumb.setAttribute('aria-selected', 'true');
        else thumb.removeAttribute('aria-selected');
      });
      this.thumbsWrapper.replaceChildren.apply(this.thumbsWrapper, thumbs);
      changed = true;
    }

    this.gridItems.forEach(function (item) {
      item.hidden = !keep(item);
    });

    this.appliedKey = selectedKey;

    if (changed) {
      trackLoading(slides.concat(thumbs));
      this.takeOverThumbs();
      this.rebuildSwiper();
    }
  };

  ColorGallery.prototype.childrenDiffer = function (wrapper, nextChildren) {
    var current = Array.prototype.slice.call(wrapper.children).filter(function (el) {
      return !isDuplicate(el);
    });
    if (current.length !== nextChildren.length) return true;
    for (var i = 0; i < nextChildren.length; i++) {
      if (current[i] !== nextChildren[i]) return true;
    }
    return false;
  };

  /**
   * Own the thumbnail behaviour.
   *
   * The theme binds a click handler to each thumbnail during Swiper init that
   * closes over the slide index AT THAT MOMENT. After filtering those indexes
   * are wrong, and re-initialising stacks another set on top. So: flag the
   * thumbnail rail as already initialised (the theme then leaves it alone) and
   * handle clicks here, in the capture phase, stopping the stale handlers from
   * ever running.
   */
  ColorGallery.prototype.takeOverThumbs = function () {
    if (this.ownsThumbs || !this.thumbsWrapper) return;
    this.ownsThumbs = true;

    var self = this;

    this.thumbsWrapper.addEventListener(
      'click',
      function (event) {
        var thumb = event.target.closest('.swiper-controls__thumbnail');
        if (!thumb || !self.thumbsWrapper.contains(thumb)) return;

        event.preventDefault();
        event.stopImmediatePropagation(); // silence the theme's stale handlers

        var index = Array.prototype.indexOf.call(self.thumbsWrapper.children, thumb);
        if (index < 0) return;

        var swiper = self.swiperComponent && self.swiperComponent.initSwiper;
        if (swiper && typeof swiper.slideTo === 'function') swiper.slideTo(index);
        self.markActiveThumb(index);
      },
      true
    );
  };

  ColorGallery.prototype.markActiveThumb = function (index) {
    if (!this.thumbsWrapper) return;
    Array.prototype.forEach.call(this.thumbsWrapper.children, function (thumb, i) {
      thumb.classList.toggle('active', i === index);
      if (i === index) thumb.setAttribute('aria-selected', 'true');
      else thumb.removeAttribute('aria-selected');
    });
  };

  /** Bring Swiper back in step with the filtered slide list. */
  ColorGallery.prototype.rebuildSwiper = function () {
    var component = this.swiperComponent;
    if (!component) return;

    var self = this;
    var main = component.initSwiper;
    var thumbs = this.thumbsEl && this.thumbsEl.swiper;

    // Clear anything Swiper's loop mode left behind before it re-measures.
    [this.slidesWrapper, this.thumbsWrapper].forEach(function (wrapper) {
      if (!wrapper) return;
      Array.prototype.slice.call(wrapper.children).forEach(function (child) {
        if (isDuplicate(child)) child.remove();
      });
    });

    // The component caches its slide list; drop it so a later init is correct.
    component._items = null;

    if (!main) {
      // Swiper has not started yet (it initialises on first scroll into view).
      // The filtered DOM is already in place, so it will come up correct.
      this.markActiveThumb(0);
      return;
    }

    refreshSwiper(main);
    refreshSwiper(thumbs);

    try {
      if (typeof main.slideTo === 'function') main.slideTo(0, 0, false);
    } catch (error) {
      log('slideTo failed', error);
    }

    // Keep the active thumbnail in step with the main slide. Bound once —
    // the instance is never replaced now, so this must not stack up.
    if (!this.boundSlideChange && typeof main.on === 'function') {
      this.boundSlideChange = true;
      try {
        main.on('slideChange', function () {
          self.markActiveThumb(this.activeIndex);
        });
      } catch (error) {
        log('slideChange bind failed', error);
        this.boundSlideChange = false;
      }
    }

    this.markActiveThumb(0);

    this.gallery.dispatchEvent(
      new CustomEvent('gallery:filtered', {
        bubbles: true,
        detail: { color: this.appliedKey }
      })
    );
  };

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  var instances = [];

  function attach() {
    document.querySelectorAll('media-gallery').forEach(function (gallery) {
      if (gallery.__colorGallery) return;
      var instance = new ColorGallery(gallery);
      gallery.__colorGallery = instance;
      instances.push(instance);
    });
  }

  /** Filter right now, in this tick, from a colour the shopper just clicked. */
  function applyNow(colorValue) {
    var key = norm(colorValue);
    if (!key) return;
    attach();
    instances.forEach(function (instance) {
      var keys = instance.colorKeys();
      if (!keys.length) return;

      // Ignore anything that is not actually one of this product's colours —
      // otherwise picking a Size or a Print Location would be read as a
      // colour change and unfilter the whole gallery.
      if (keys.indexOf(key) === -1) return;

      instance.manualKey = key;
      instance.apply(key, keys, false);
      // Snapshot what the controls read RIGHT NOW, so the follow-up pass only
      // reacts to a control that changes after this click.
      instance.rememberControls();
    });
  }

  /** Filter immediately, with no debounce. Used for the very first pass. */
  function syncNow() {
    attach();
    instances.forEach(function (instance) {
      instance.sync(false);
    });
  }

  var pending = null;
  function syncSoon() {
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () {
      pending = null;
      syncNow();
    }, DEBOUNCE_MS);
  }

  function bind() {
    injectStyles();

    // Filter straight away. The theme loads this file after the gallery
    // markup, so the slides already exist and Ella's picker has rendered
    // server-side — waiting for DOMContentLoaded plus a debounce would show
    // every colour for a moment before collapsing to one.
    syncNow();

    // A swatch click is applied synchronously, so the gallery turns over at
    // the same instant as the main image rather than a moment later.
    document.addEventListener(
      'click',
      function (event) {
        var swatch = event.target.closest && event.target.closest(ANY_SWATCH);
        if (!swatch) return;
        applyNow(swatch.getAttribute('data-color'));
      },
      true
    );

    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target.closest || !target.closest('variant-selects')) return;
      if (target.value) applyNow(target.value);
      syncSoon();
    });

    // The Roman Imprint carousel is injected after page load, and the theme
    // re-renders its option markup after a variant fetch. Watch for both.
    new MutationObserver(syncSoon).observe(document.body, {
      childList: true,
      subtree: true
    });

    document.addEventListener('variant:update', syncSoon);
    document.addEventListener('variantChange', syncSoon);
    window.addEventListener('pageshow', syncSoon);

    // Late arrivals: the Roman Imprint carousel and any app-injected media.
    document.addEventListener('DOMContentLoaded', syncSoon);
    window.addEventListener('load', syncSoon);
  }

  // Run as early as the DOM allows. document.body is needed for the observer;
  // with a deferred script it is already there, so this is effectively
  // immediate and the gallery is filtered before the first paint.
  if (document.body) {
    bind();
  } else {
    document.addEventListener('DOMContentLoaded', bind);
  }

  /* ------------------------------------------------------------------ */
  /* Keep the custom element defined (theme markup relies on the tag)    */
  /* ------------------------------------------------------------------ */

  if (!customElements.get('media-gallery')) {
    customElements.define(
      'media-gallery',
      class MediaGallery extends HTMLElement {
        connectedCallback() {
          syncSoon();
        }

        /** Force the gallery to a colour, e.g. from the console. */
        filterByColor(color) {
          attach();
          if (!this.__colorGallery) return;
          this.__colorGallery.apply(norm(color), this.__colorGallery.colorKeys(), true);
        }
      }
    );
  }
})();
